import { BadRequestException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { createHash } from 'node:crypto';
import { PrismaService, S3CompatibleAdapter, StorageAdapter } from '@cart/api-data-access';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';

const BACKUP_BATCH = 50;

export interface BackupDestination {
  bucket: string;
  region: string;
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
}

/**
 * Cross-provider media backup (plan §15, Phase 4).
 *
 * Deliberately *not* the first line of defence. Object versioning plus a
 * lifecycle rule on the primary bucket covers the losses that actually happen
 * — an accidental delete, a bad overwrite, a bug in our own purge job — at
 * near-zero cost. This service exists for the case versioning cannot cover:
 * losing the provider or the account itself, which needs bytes sitting
 * somewhere with a different login.
 *
 * The manifest lives in Postgres (`MediaBackupRun` / `MediaBackupEntry`),
 * never only in the bucket it describes — a manifest stored exclusively inside
 * the thing it is meant to help you recover is not a manifest.
 */
@Injectable()
export class MediaBackupService {
  private readonly logger = new Logger(MediaBackupService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver
  ) {}

  /**
   * The backup destination, built from the environment.
   *
   * `protected` rather than private so a spec can substitute a fake without a
   * DI token: the destination is the one collaborator here that talks to a
   * third-party network, and every test of this service would otherwise try
   * to reach a real bucket.
   */
  protected destination(): StorageAdapter | null {
    const bucket = process.env.BACKUP_BUCKET;
    const accessKeyId = process.env.BACKUP_ACCESS_KEY_ID;
    const secretAccessKey = process.env.BACKUP_SECRET_ACCESS_KEY;
    const endpoint = process.env.BACKUP_ENDPOINT;
    if (!bucket || !accessKeyId || !secretAccessKey || !endpoint) return null;

    return new S3CompatibleAdapter({
      bucket,
      region: process.env.BACKUP_REGION ?? 'auto',
      endpoint,
      accessKeyId,
      secretAccessKey,
      forcePathStyle: true,
    });
  }

  isConfigured(): boolean {
    return this.destination() !== null;
  }

  /**
   * Incremental by default: an asset already recorded as copied in a previous
   * successful run is skipped, because media keys are immutable (a replace
   * writes a new key), so "already backed up" genuinely means "unchanged".
   */
  async runBackup(organizationId: string, kind: 'full' | 'incremental' = 'incremental') {
    const destination = this.destination();
    if (!destination) {
      throw new BadRequestException('No backup destination is configured (BACKUP_* environment variables).');
    }

    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: { organizationId, status: { in: ['active', 'retained'] } },
      select: { id: true },
    });
    if (!configs.length) throw new BadRequestException('This organization has no storage backends');

    const run = await this.prisma.mediaBackupRun.create({
      data: {
        organizationId,
        sourceConfigId: configs[0].id,
        kind,
        status: 'running',
      },
    });

    const alreadyCopied =
      kind === 'incremental' ? await this.copiedAssetIds(organizationId) : new Set<string>();

    let objectCount = 0;
    let byteCount = 0;
    let failedCount = 0;
    let cursorId: string | undefined;

    try {
      for (;;) {
        const batch = await this.prisma.mediaAsset.findMany({
          where: {
            organizationId,
            status: { in: ['active', 'trashed'] },
            ...(cursorId ? { id: { gt: cursorId } } : {}),
          },
          orderBy: { id: 'asc' },
          take: BACKUP_BATCH,
        });
        if (!batch.length) break;
        cursorId = batch[batch.length - 1].id;

        for (const asset of batch) {
          if (alreadyCopied.has(asset.id)) {
            await this.prisma.mediaBackupEntry.create({
              data: {
                runId: run.id,
                assetId: asset.id,
                storageKey: asset.storageKey,
                sizeBytes: asset.sizeBytes,
                checksum: asset.checksum,
                status: 'skipped',
              },
            });
            continue;
          }

          try {
            const sourceAdapter = await this.resolver.forConfigId(asset.storageConfigId, 'backup');
            const bytes = await sourceAdapter.read(asset.storageKey);
            const checksum = createHash('sha256').update(bytes).digest('hex');

            // Namespaced by run date so a restore can pick a point in time
            // rather than only "latest".
            const backupKey = `${asset.organizationId}/${asset.storageKey}`;
            await destination.save(bytes, backupKey, asset.mimeType);

            await this.prisma.mediaBackupEntry.create({
              data: {
                runId: run.id,
                assetId: asset.id,
                storageKey: asset.storageKey,
                sizeBytes: bytes.length,
                checksum,
                status: 'copied',
              },
            });
            objectCount += 1;
            byteCount += bytes.length;
          } catch (err) {
            failedCount += 1;
            await this.prisma.mediaBackupEntry.create({
              data: {
                runId: run.id,
                assetId: asset.id,
                storageKey: asset.storageKey,
                sizeBytes: asset.sizeBytes,
                status: 'failed',
                error: err instanceof Error ? err.message : String(err),
              },
            });
          }
        }
      }

      return this.prisma.mediaBackupRun.update({
        where: { id: run.id },
        data: {
          status: failedCount > 0 ? 'failed' : 'completed',
          objectCount,
          byteCount: BigInt(byteCount),
          failedCount,
          finishedAt: new Date(),
          error: failedCount > 0 ? `${failedCount} object(s) failed` : null,
        },
      });
    } catch (err) {
      await this.prisma.mediaBackupRun.update({
        where: { id: run.id },
        data: {
          status: 'failed',
          error: err instanceof Error ? err.message : String(err),
          finishedAt: new Date(),
        },
      });
      throw err;
    }
  }

  private async copiedAssetIds(organizationId: string): Promise<Set<string>> {
    const entries = await this.prisma.mediaBackupEntry.findMany({
      where: {
        status: 'copied',
        run: { organizationId, status: 'completed' },
      },
      select: { assetId: true },
    });
    return new Set(entries.map(e => e.assetId).filter((id): id is string => id !== null));
  }

  /** Nightly, for every org that has media. Skipped entirely when unconfigured. */
  @Cron('0 2 * * *')
  async nightlyBackup(): Promise<void> {
    if (!this.isConfigured()) return;

    const orgs = await this.prisma.organization.findMany({
      where: { storageAssetCount: { gt: 0 }, deletedAt: null },
      select: { id: true },
    });

    for (const org of orgs) {
      try {
        await this.runBackup(org.id, 'incremental');
      } catch (err) {
        this.logger.error(`Nightly backup failed for org ${org.id}: ${err}`);
      }
    }
  }

  /**
   * Restores one asset from backup.
   *
   * Writes to a **new key** and never overwrites the live object: a restore
   * that silently replaces production data is indistinguishable from the
   * accident it was meant to undo. The caller repoints the owner record once
   * it has seen what came back.
   */
  async restoreAsset(assetId: string, organizationId: string, actorId = 'system') {
    const destination = this.destination();
    if (!destination) throw new BadRequestException('No backup destination is configured');

    const asset = await this.prisma.mediaAsset.findUnique({ where: { id: assetId } });
    if (!asset || asset.organizationId !== organizationId) {
      throw new NotFoundException(`Media asset ${assetId} not found`);
    }

    const entry = await this.prisma.mediaBackupEntry.findFirst({
      where: { assetId, status: 'copied' },
      orderBy: { createdAt: 'desc' },
    });
    if (!entry) throw new NotFoundException('No backup entry exists for this asset');

    const bytes = await destination.read(`${organizationId}/${entry.storageKey}`);
    if (entry.checksum) {
      const actual = createHash('sha256').update(bytes).digest('hex');
      if (actual !== entry.checksum) {
        throw new BadRequestException('Backup object failed checksum verification — refusing to restore it');
      }
    }

    const targetConfig = await this.resolver.activeConfig(organizationId);
    if (!targetConfig) throw new BadRequestException('No active storage backend to restore into');
    const targetAdapter = await this.resolver.forConfig(targetConfig, 'restore');

    const restoredKey = `${asset.storageKey.replace(/(\.[^.]+)?$/, '')}_restored_${Date.now()}`;
    const saved = await targetAdapter.save(bytes, restoredKey, asset.mimeType);

    return this.prisma.mediaAsset.create({
      data: {
        organizationId,
        storeId: asset.storeId,
        ownerType: asset.ownerType,
        ownerId: asset.ownerId,
        storageConfigId: targetConfig.id,
        storageKey: saved.storageKey,
        publicUrl: asset.publicUrl ? saved.url : null,
        fileName: asset.fileName,
        mimeType: asset.mimeType,
        sizeBytes: saved.sizeBytes,
        checksum: saved.checksum ?? null,
        status: 'active',
        createdBy: actorId,
        updatedBy: actorId,
      },
    });
  }

  async listRuns(organizationId: string, limit = 20) {
    return this.prisma.mediaBackupRun.findMany({
      where: { organizationId },
      orderBy: { startedAt: 'desc' },
      take: limit,
    });
  }

  async getRun(runId: string, organizationId: string) {
    const run = await this.prisma.mediaBackupRun.findUnique({
      where: { id: runId },
      include: { entries: { take: 500, orderBy: { createdAt: 'asc' } } },
    });
    if (!run || run.organizationId !== organizationId) {
      throw new NotFoundException(`Backup run ${runId} not found`);
    }
    return run;
  }

  /**
   * Quarterly restore drill.
   *
   * A backup that has never been restored is not a backup — it is an untested
   * assumption with a storage bill. This picks the most recent completed run,
   * pulls one object back, verifies its checksum, and records the result so
   * "when did a restore last actually work" is a number on a dashboard rather
   * than a hope.
   */
  @Cron('0 5 1 */3 *')
  async restoreDrill(): Promise<{ ok: boolean; detail: string }> {
    if (!this.isConfigured()) return { ok: false, detail: 'No backup destination configured' };

    const entry = await this.prisma.mediaBackupEntry.findFirst({
      where: { status: 'copied' },
      orderBy: { createdAt: 'desc' },
      include: { run: true },
    });
    if (!entry) return { ok: false, detail: 'No completed backup entries to drill against' };

    try {
      const destination = this.destination() as StorageAdapter;
      const bytes = await destination.read(`${entry.run.organizationId}/${entry.storageKey}`);
      const actual = createHash('sha256').update(bytes).digest('hex');
      const ok = !entry.checksum || actual === entry.checksum;
      const detail = ok
        ? `Restored ${entry.storageKey} (${bytes.length} bytes), checksum verified`
        : `Checksum mismatch for ${entry.storageKey}`;
      this.logger[ok ? 'log' : 'error'](`Restore drill: ${detail}`);
      return { ok, detail };
    } catch (err) {
      const detail = `Restore drill failed: ${err instanceof Error ? err.message : String(err)}`;
      this.logger.error(detail);
      return { ok: false, detail };
    }
  }
}
